// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Modal from 'antd/lib/modal';
import notification from 'antd/lib/notification';

import { ThunkDispatch } from 'utils/redux';
import { modelsActions, startInferenceAsync } from 'actions/models-actions';
import CVATLoadingSpinner from 'components/common/loading-spinner';
import { CombinedState } from 'reducers';
import MLModel from 'cvat-core/src/ml-model';
import { getCore, Task, Job } from 'cvat-core-wrapper';
import DetectorRunner from './detector-runner';

const core = getCore();

interface StateToProps {
    visible: boolean;
    instance: any;
    detectors: MLModel[];
    reid: MLModel[];
    classifiers: MLModel[];
}

interface DispatchToProps {
    runInference(instance: any, model: MLModel, body: object): void;
    closeDialog(): void;
}

function mapStateToProps(state: CombinedState): StateToProps {
    const { models } = state;
    const { detectors, reid, classifiers } = models;

    return {
        visible: models.modelRunnerIsVisible,
        instance: models.modelRunnerInstance,
        reid,
        detectors,
        classifiers,
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch): DispatchToProps {
    return {
        runInference(instance: any, model: MLModel, body: object) {
            dispatch(startInferenceAsync(instance, model, body));
        },
        closeDialog() {
            dispatch(modelsActions.closeRunModelDialog());
        },
    };
}

function ModelRunnerDialog(props: StateToProps & DispatchToProps): JSX.Element {
    const {
        reid, detectors, classifiers, instance, visible, runInference, closeDialog,
    } = props;

    const models = [...reid, ...detectors, ...classifiers];
    const [xInstance, setXInstance] = useState<any | null>(null);

    useEffect(() => {
        if (instance instanceof Task) {
            core.tasks.get({ id: instance.id }).then(([_task]: Task[]) => {
                if (_task) {
                    setXInstance(_task);
                }
            }).catch((error: any) => {
                notification.error({ message: '无法获取任务详情', description: error.toString() });
            });
        } else if (instance instanceof Job) {
            core.jobs.get({ jobID: instance.id }).then(([_job]: Job[]) => {
                if (_job) {
                    setXInstance(_job);
                }
            }).catch((error: any) => {
                notification.error({ message: '无法获取作业详情', description: error.toString() });
            });
        }
    }, [visible, instance]);

    return (
        <Modal
            destroyOnClose
            visible={visible}
            footer={[]}
            onCancel={(): void => closeDialog()}
            maskClosable
            title='自动标注'
        >
            { xInstance ? (
                <DetectorRunner
                    withCleanup
                    models={models}
                    labels={xInstance.labels}
                    dimension={xInstance.dimension}
                    runInference={(...args) => {
                        closeDialog();
                        runInference(xInstance, ...args);
                    }}
                />
            ) : <CVATLoadingSpinner /> }

        </Modal>
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(ModelRunnerDialog);
